// Mirror of backend/src/calc.js so the UI can recompute optimistically without
// a round-trip while the user drags sliders.

const HOURS_PER_PW = 40;
const MONTHS_PER_WEEK = 1 / 4.33;

export function durationMonths(effortPw, fteAllocation) {
  const fte = Math.max(0.05, Number(fteAllocation) || 0);
  const pw  = Math.max(0,    Number(effortPw)      || 0);
  return (pw / fte) * MONTHS_PER_WEEK;
}

export function taskCostSek(effortPw, hourlyRateSek) {
  const pw = Math.max(0, Number(effortPw) || 0);
  return pw * HOURS_PER_PW * Number(hourlyRateSek);
}

export function monthlyValueSek(task, settings) {
  const hours = Number(task.monthlyHoursSaved) || 0;
  const rev   = Number(task.newRevenueSek)     || 0;
  return hours * Number(settings.hourlyRateSek) + rev;
}

export function weightedAvgScore(task, settings) {
  const sQ = Number(task.scoreQuality)   || 0;
  const sS = Number(task.scoreStrategic) || 0;
  const sC = Number(task.scoreCustomer)  || 0;
  const fQ = Math.max(0, Number(settings.factorQuality)   || 0);
  const fS = Math.max(0, Number(settings.factorStrategic) || 0);
  const fC = Math.max(0, Number(settings.factorCustomer)  || 0);
  const sum = fQ + fS + fC;
  if (sum > 0) return (sQ * fQ + sS * fS + sC * fC) / sum;
  return (sQ + sS + sC) / 3;
}

export function taskEconomics(task, settings, horizonMonths = 20) {
  const start = Number(task.startMonth);
  const dur   = durationMonths(task.effortPw, task.fteAllocation);
  const compl = start + dur;
  const cost  = taskCostSek(task.effortPw, settings.hourlyRateSek);
  const mv    = monthlyValueSek(task, settings);

  const monthsToBE = mv > 0 ? cost / mv : Infinity;
  const beMonth    = compl + monthsToBE;
  const monthsRun  = Math.max(0, horizonMonths - compl);
  const endValue   = monthsRun * mv - cost;

  const newRev = Number(task.newRevenueSek) || 0;

  return {
    durationMonths:      dur,
    completionMonth:     compl,
    costSek:             cost,
    monthlyValueSek:     mv,
    monthsToBreakeven:   monthsToBE,
    breakevenMonth:      beMonth,
    horizonEndValueSek:  endValue,
    monthsRunToHorizon:  monthsRun,                 // months the task is live before horizon end
    newRevenueHorizonSek: newRev * monthsRun,       // total new revenue while live, to horizon
    weightedAvgScore:    weightedAvgScore(task, settings),
    valueBreakdown: {
      hoursSavedSek: (Number(task.monthlyHoursSaved)||0) * Number(settings.hourlyRateSek),
      newRevenueSek:  Number(task.newRevenueSek)    ||0,
    },
  };
}

export function portfolioSummary(tasks, settings, horizonMonths = 20) {
  let totalCost = 0, totalMV = 0, totalEnd = 0, totalHours = 0;
  let totalHoursValue = 0, totalNewRevMonthly = 0, totalNewRevHorizon = 0;
  let scoreSum = 0, activeCount = 0;
  // Inactive tasks are excluded from aggregates (see backend/src/calc.js).
  for (const t of tasks) {
    if (t.active === false || t.active === 0) continue;
    const e = taskEconomics(t, settings, horizonMonths);
    totalCost  += e.costSek;
    totalMV    += e.monthlyValueSek;
    totalEnd   += e.horizonEndValueSek;
    totalHours += Number(t.monthlyHoursSaved) || 0;
    totalHoursValue    += e.valueBreakdown.hoursSavedSek;
    totalNewRevMonthly += e.valueBreakdown.newRevenueSek;
    totalNewRevHorizon += e.newRevenueHorizonSek;
    scoreSum += e.weightedAvgScore;
    activeCount++;
  }
  return {
    totalCostSek: totalCost,
    totalMonthlyValueSek: totalMV,
    totalHorizonEndValueSek: totalEnd,
    totalMonthlyHoursSaved: totalHours,
    totalHoursSavedValueSek: totalHoursValue,
    totalNewRevenueMonthlySek: totalNewRevMonthly,
    totalNewRevenueHorizonSek: totalNewRevHorizon,
    avgScore: activeCount > 0 ? scoreSum / activeCount : 0,
    portfolioBreakevenMonths: totalMV > 0 ? totalCost / totalMV : Infinity,
  };
}

/** Convert month index (0 = May 2026) to "Mmm YYYY" string. */
export function fmtMonth(m, anchorYear = 2026, anchorMonth = 4 /* May = 4 zero-indexed */) {
  if (!Number.isFinite(m)) return "—";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const totalMonths = Math.round(anchorMonth + m);
  const y = anchorYear + Math.floor(totalMonths / 12);
  const mm = ((totalMonths % 12) + 12) % 12;
  return `${months[mm]} ${y}`;
}

/** Format SEK with thousand separators. */
export function fmtSek(n) {
  if (!Number.isFinite(n)) return "—";
  const sign = n < 0 ? "−" : "";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return sign + (abs/1_000_000).toFixed(2).replace(/\.?0+$/,"") + " M SEK";
  if (abs >= 1_000)     return sign + Math.round(abs).toLocaleString("sv-SE") + " SEK";
  return sign + Math.round(abs) + " SEK";
}

export function fmtHours(n) {
  if (!Number.isFinite(n)) return "—";
  const r = Math.round(n);
  return r.toLocaleString("sv-SE") + " h";
}

export function fmtMonths(n) {
  if (!Number.isFinite(n)) return "—";
  if (n === Infinity) return "never";
  if (n < 1) return (n * 30).toFixed(0) + " days";
  if (n < 12) return n.toFixed(1) + " months";
  return (n/12).toFixed(1) + " years";
}
